/**
 * Sign-in and sign-out as one contract.
 *
 * Two separate problems, and they pull in opposite directions.
 *
 * A signed-out device must stop attributing events to the account that just
 * left, or the next person to use it — or the same person browsing anonymously
 * — lands on the previous account. That needs a hard reset of the persisted
 * distinct id.
 *
 * But a human with two Highlight accounts is still one human, and resetting
 * throws away the only evidence of that. So the outgoing account id is kept on
 * the device across the reset and handed to the next sign-in, which records it
 * as a **property** rather than merging anything.
 *
 * The distinction is the whole point. `alias()` is irreversible in PostHog:
 * aliasing two real accounts collapses them into one person for good, and a
 * work and a personal account become indistinguishable. A property leaves the
 * accounts separate and still lets a query walk the chain.
 */

/**
 * Where the outgoing account id waits between sign-out and the next sign-in.
 *
 * Deliberately not implemented here: the browser has `localStorage`, the
 * desktop main process has `GlobalStore`, and a Worker has nowhere at all,
 * since it has no device to remember on.
 */
export interface IdentityHandoffStore {
  read(): string | undefined
  write(userId: string | undefined): void
}

/**
 * Remembers nothing, so linking simply does not happen.
 *
 * The default on purpose. An in-memory store would link two accounts within
 * one process and silently stop at the next restart, which is both the common
 * case and the one nobody would notice was broken. A runtime that wants
 * linking supplies real storage.
 */
export function noHandoffStore(): IdentityHandoffStore {
  return { read: () => undefined, write: () => {} }
}

/** In-memory, for tests that exercise the handoff itself. Survives nothing else. */
export function memoryHandoffStore(): IdentityHandoffStore {
  let held: string | undefined
  return {
    read: () => held,
    write: (userId) => {
      held = userId
    },
  }
}

/**
 * Person properties that make multi-account use answerable.
 *
 * `device_id` is the join key: every account signed into on one device carries
 * the same value, so accounts sharing a human can be grouped without ever
 * merging them. `previous_logged_in_user_id` gives that grouping an order.
 */
export interface IdentityLinkProperties {
  device_id?: string
  previous_logged_in_user_id?: string
}

export function identityLinkProperties(identity: {
  userId?: string
  anonymousId?: string
  previousUserId?: string
}): IdentityLinkProperties {
  const link: IdentityLinkProperties = {}

  if (identity.anonymousId) {
    link.device_id = identity.anonymousId
  }

  // Only when it is genuinely a different account. The same user signing back
  // in is not a multi-account signal, and recording it would make the property
  // useless for the question it exists to answer.
  if (identity.previousUserId && identity.previousUserId !== identity.userId) {
    link.previous_logged_in_user_id = identity.previousUserId
  }

  return link
}
